const sourceOffsets: Record<string, number> = {
  UTC: 0,
  GMT: 0,
  BST: 60,
  CET: 60,
  CEST: 120,
  EET: 120,
  EEST: 180,
  MSK: 180,
};

export function userTimeZone() {
  const detected = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  try {
    const configured = window.localStorage.getItem("fasterpedia.timeZone");
    if (configured) {
      new Intl.DateTimeFormat(undefined, { timeZone: configured }).format();
      return configured;
    }
  } catch {
    // Storage can be unavailable in privacy-focused browser configurations.
  }
  return detected;
}

export function parseDateTime(value: string) {
  const normalized = value.trim();
  if (!normalized) return undefined;
  const liquipedia = normalized.match(/^(\d{4})-(\d{2})-(\d{2})\s+-\s+(\d{2}):(\d{2})(?:\s+([A-Z]{2,5}|[+-]\d{2}:?\d{2}))?$/);
  if (liquipedia) {
    const [, year, month, day, hour, minute, zone = "UTC"] = liquipedia;
    let offset = sourceOffsets[zone];
    if (offset === undefined && /^[+-]/.test(zone)) {
      const sign = zone.startsWith("-") ? -1 : 1;
      const digits = zone.slice(1).replace(":", "");
      offset = sign * (Number(digits.slice(0, 2)) * 60 + Number(digits.slice(2, 4)));
    }
    if (offset !== undefined) return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute)) - offset * 60_000);
  }
  const timestamp = Date.parse(normalized);
  return Number.isNaN(timestamp) ? undefined : new Date(timestamp);
}

export function formatDateTime(value: string) {
  const parsed = parseDateTime(value);
  if (!parsed) return value;
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: userTimeZone(),
    timeZoneName: "short",
  }).format(parsed);
}

export function formatDate(value: string) {
  const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return value;
  const [, year, month, day] = match;
  return new Intl.DateTimeFormat(undefined, { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" }).format(new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), 12)));
}

export function dateTimeSortValue(value: string) {
  return parseDateTime(value)?.getTime() ?? Number.MAX_SAFE_INTEGER;
}

export function countdownLabel(milliseconds: number) {
  const totalMinutes = Math.max(1, Math.ceil(milliseconds / 60_000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `Starts in ${minutes}m`;
  if (minutes === 0) return `Starts in ${hours}h`;
  return `Starts in ${hours}h ${minutes}m`;
}

export function elapsedLabel(milliseconds: number) {
  const totalMinutes = Math.max(1, Math.floor(milliseconds / 60_000));
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `Started ${days}d ${hours}h ago`;
  if (hours > 0 && minutes > 0) return `Started ${hours}h ${minutes}m ago`;
  if (hours > 0) return `Started ${hours}h ago`;
  return `Started ${totalMinutes}m ago`;
}
